import type { Locale } from './locales';
import { LOCALES } from './locales';

/**
 * Messages live in apps/web/messages/<lang>/<namespace>.json (namespace = screen).
 * The loader merges all namespaces of a language into flat ids "<namespace>.<key>".
 * Nested objects inside a file are flattened with dots too.
 * Adding a namespace = adding the JSON file in all 4 languages; nothing to register here.
 */
type Json = string | { [k: string]: Json };
export type Messages = Record<string, string>;

const files = import.meta.glob<Record<string, Json>>('../../messages/*/*.json', {
  eager: true,
  import: 'default',
});

function flatten(prefix: string, obj: Record<string, Json>, out: Messages): void {
  for (const [k, v] of Object.entries(obj)) {
    const id = `${prefix}.${k}`;
    if (typeof v === 'string') out[id] = v;
    else flatten(id, v, out);
  }
}

export function buildMessages(input: Record<string, Record<string, Json>>): Record<Locale, Messages> {
  const all = Object.fromEntries(LOCALES.map((l) => [l, {} as Messages])) as Record<Locale, Messages>;
  for (const [path, data] of Object.entries(input)) {
    const m = /messages\/([^/]+)\/([^/]+)\.json$/.exec(path);
    if (!m) continue;
    const lang = m[1];
    const ns = m[2];
    if (!lang || !ns || !(lang in all)) continue;
    flatten(ns, data, all[lang as Locale]);
  }
  return all;
}

export const MESSAGES: Record<Locale, Messages> = buildMessages(files);

export function getMessages(locale: Locale): Messages {
  // English is the fallback for an id missing in another language (check-i18n forbids that in CI).
  return locale === 'en' ? MESSAGES.en : { ...MESSAGES.en, ...MESSAGES[locale] };
}
